import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Send, Loader2 } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

const WORKER_URL = 'https://utxomacro-proxy.utxomacro.workers.dev';

interface ContactProps {
  onNavigate: (page: string) => void;
}

const Contact: React.FC<ContactProps> = ({ onNavigate }) => {
  useSEO({
    title: 'Contact',
    description: 'Get in touch with UTXOMacro — questions, feedback, collaboration, or media inquiries.',
    url: 'https://utxomacro.com/contact',
    keywords: 'contact UTXOMacro, Bitcoin media inquiry, feedback',
  });
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${WORKER_URL}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json() as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) throw new Error(data.error ?? 'Something went wrong');
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message ?? 'Failed to send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0A0A0A' }}>
      <Navbar currentPage="contact" onNavigate={onNavigate} />
      <div className="flex-1 flex flex-col items-center justify-center px-4 pt-16 py-20">
        <div className="w-full max-w-lg">
          <h1 className="text-3xl font-bold text-white mb-2">Get in Touch</h1>
          <p className="text-gray-500 text-sm mb-8">
            Research inquiries, partnerships, or just want to talk Bitcoin? Send a message.
          </p>

          {submitted ? (
            <div className="bg-[#00C896]/10 border border-[#00C896]/30 rounded-xl px-6 py-5 text-[#00C896] font-medium">
              Message received. We'll get back to you within 24 hours.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div>
                <label className="block text-gray-500 text-xs font-semibold uppercase tracking-widest mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Satoshi Nakamoto"
                  required
                  className="w-full bg-[#111] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-700 text-sm focus:outline-none focus:border-[#F7931A]/50 transition-colors"
                />
              </div>
              <div>
                <label className="block text-gray-500 text-xs font-semibold uppercase tracking-widest mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="satoshi@bitcoin.org"
                  required
                  className="w-full bg-[#111] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-700 text-sm focus:outline-none focus:border-[#F7931A]/50 transition-colors"
                />
              </div>
              <div>
                <label className="block text-gray-500 text-xs font-semibold uppercase tracking-widest mb-2">
                  Message
                </label>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Your message..."
                  required
                  rows={5}
                  className="w-full bg-[#111] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-700 text-sm focus:outline-none focus:border-[#F7931A]/50 transition-colors resize-none"
                />
              </div>
              {error && (
                <p className="text-red-400 text-sm border border-red-500/30 bg-red-500/10 rounded-lg px-4 py-3">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center justify-center gap-2 bg-[#F7931A] text-black font-semibold text-sm px-6 py-3 rounded-lg hover:bg-[#e8871a] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting
                  ? <><Loader2 size={15} className="animate-spin" /> Sending…</>
                  : <><Send size={15} /> Send Message</>}
              </button>
            </form>
          )}
        </div>
      </div>
      <Footer onNavigate={onNavigate} />
    </div>
  );
};

export default Contact;
